"use client";

import { useEffect } from "react";
import Script from "next/script";

export default function MailchimpSubscribe() {
  useEffect(() => {
    let isSubmitting = false;

    // Function to toggle subscribe button based on checkbox and subscription state
    const toggleSubscribeButton = () => {
      const checkbox = document.querySelector(
        'input[name="gdpr[192]"]',
      ) as HTMLInputElement;
      const submitButton = document.getElementById(
        "mc-embedded-subscribe",
      ) as HTMLButtonElement;

      if (checkbox && submitButton) {
        // If submitting, show loading state
        if (isSubmitting) {
          submitButton.disabled = true;
          submitButton.style.backgroundColor = "#48DBB2";
          submitButton.style.cursor = "not-allowed";
          submitButton.style.opacity = "0.8";
          submitButton.value = "Loading...";
          return;
        }

        // Normal state - check if checkbox is checked
        if (checkbox.checked) {
          submitButton.disabled = false;
          submitButton.style.backgroundColor = "#48DBB2";
          submitButton.style.cursor = "pointer";
          submitButton.style.opacity = "1";
          submitButton.value = "Subscribe";
        } else {
          submitButton.disabled = true;
          submitButton.style.backgroundColor = "#48DBB2";
          submitButton.style.cursor = "not-allowed";
          submitButton.style.opacity = "0.8";
          submitButton.value = "Subscribe";
        }
      }
    };

    // Function to hide error/success messages when user interacts with form
    const hideMessages = () => {
      const errorResponse = document.getElementById("mce-error-response");
      const successResponse = document.getElementById("mce-success-response");

      if (errorResponse) {
        errorResponse.style.display = "none";
      }
      if (successResponse) {
        successResponse.style.display = "none";
      }
    };

    // Function to handle form submission
    const handleSubmit = (e: Event) => {
      const form = e.target as HTMLFormElement;
      const emailInput = document.getElementById(
        "mce-EMAIL",
      ) as HTMLInputElement;
      const checkbox = document.querySelector(
        'input[name="gdpr[192]"]',
      ) as HTMLInputElement;

      // Validate email and checkbox
      if (!emailInput.value || !checkbox.checked) {
        return;
      }

      // Set loading state
      isSubmitting = true;
      toggleSubscribeButton();

      // Monitor for success/error messages
      const checkForResponse = setInterval(() => {
        const errorResponse = document.getElementById("mce-error-response");
        const successResponse = document.getElementById("mce-success-response");

        // Check if error is displayed
        if (
          errorResponse &&
          errorResponse.style.display !== "none" &&
          errorResponse.textContent?.trim()
        ) {
          isSubmitting = false;
          toggleSubscribeButton();
          clearInterval(checkForResponse);
        }

        // Check if success is displayed
        if (
          successResponse &&
          successResponse.style.display !== "none" &&
          successResponse.textContent?.trim()
        ) {
          isSubmitting = false;

          // Reset form so user can submit again
          if (form) {
            form.reset();
          }

          // Restore button state
          const submitButton = document.getElementById(
            "mc-embedded-subscribe",
          ) as HTMLButtonElement;

          if (submitButton) {
            submitButton.disabled = true;
            submitButton.value = "Subscribe";
            submitButton.style.opacity = "0.6";
            submitButton.style.cursor = "not-allowed";
          }

          clearInterval(checkForResponse);
        }
      }, 100);

      // Clear interval after 10 seconds (timeout)
      setTimeout(() => {
        clearInterval(checkForResponse);
        if (isSubmitting) {
          isSubmitting = false;
          toggleSubscribeButton();
        }
      }, 10000);
    };

    // Set initial state (disabled)
    toggleSubscribeButton();

    // Add event listener to checkbox
    const checkbox = document.querySelector('input[name="gdpr[192]"]');
    if (checkbox) {
      checkbox.addEventListener("change", toggleSubscribeButton);
    }

    // Add event listeners to hide error messages when user starts typing or changes checkbox
    const emailInput = document.getElementById("mce-EMAIL");
    if (emailInput) {
      emailInput.addEventListener("input", hideMessages);
      emailInput.addEventListener("focus", hideMessages);
    }

    if (checkbox) {
      checkbox.addEventListener("change", hideMessages);
    }

    // Add form submit listener
    const form = document.getElementById("mc-embedded-subscribe-form");
    if (form) {
      form.addEventListener("submit", handleSubmit);
    }

    return () => {
      if (checkbox) {
        checkbox.removeEventListener("change", toggleSubscribeButton);
        checkbox.removeEventListener("change", hideMessages);
      }
      if (emailInput) {
        emailInput.removeEventListener("input", hideMessages);
        emailInput.removeEventListener("focus", hideMessages);
      }
      if (form) {
        form.removeEventListener("submit", handleSubmit);
      }
    };
  }, []);

  return (
    <>
      <link
        href="//cdn-images.mailchimp.com/embedcode/classic-061523.css"
        rel="stylesheet"
        type="text/css"
      />

      <style jsx>{`
        #mce-responses div#mce-responses {
          margin: 0 !important;
        }

        #mc_embed_signup {
          background: transparent;
          clear: left;
          font:
            14px Helvetica,
            Arial,
            sans-serif;
          max-width: 1050px;
          margin: 0 auto;
        }

        .custom-form-container {
          display: flex;
          width: 750px;
          height: 50px;
          gap: 7px;
          margin: 0 auto;
          max-width: 100%;
        }

        @media (max-width: 768px) {
          .custom-form-container {
            width: 550px;
            height: 55px;
          }

          :global(#mc_embed_shell) {
            padding: 32px 0 !important;
          }

          h4 {
            font-size: 20px !important;
            margin-bottom: 20px !important;
          }

          :global(#mergeRow-gdpr) {
            margin-top: 15px;
            padding: 15px 0;
          }

          :global(#mergeRow-gdpr .content__gdpr label) {
            font-size: 16px;
          }

          :global(#mergeRow-gdpr .content__gdpr p) {
            font-size: 13px;
          }

          :global(.checkbox.subfield span) {
            font-size: 14px;
          }

          :global(.content__gdprLegal p) {
            font-size: 11px;
          }
        }

        @media (min-width: 769px) and (max-width: 1023px) {
          :global(#mc_embed_shell) {
            padding: 45px 0 60px 0 !important;
          }
        }

        .custom-email-input {
          font-family: "Poppins", sans-serif !important;
          flex: 1;
          padding: 10px 25px !important;
          border-radius: 20px 0 0 20px !important;
          background-color: #d9d9d9 !important;
          color: #808080 !important;
          border: none !important;
          font-weight: 600 !important;
          font-size: 17px !important;
          outline: none !important;
          width: calc(100% - 200px) !important;
          height: 100% !important;
          box-sizing: border-box !important;
        }

        .custom-email-input::placeholder {
          color: #808080 !important;
        }

        @media (max-width: 768px) {
          .custom-email-input {
            padding: 8px 12px !important;
            font-size: 14px !important;
            width: calc(100% - 100px) !important;
          }
        }

        @media (min-width: 1024px) {
          .custom-email-input {
            width: calc(100% - 400px) !important;
          }
        }

        .custom-subscribe-btn {
          font-family: "Poppins", sans-serif !important;
          padding: 10px 18px !important;
          height: 100% !important;
          width: 200px !important;
          font-weight: bold !important;
          color: white !important;
          border-radius: 0 20px 20px 0 !important;
          background-color: #48dbb2 !important;
          border: none !important;
          font-size: 19px !important;
          cursor: pointer !important;
          transition: background-color 0.3s ease !important;
        }

        .custom-subscribe-btn:hover {
          background-color: #3bc4a0 !important;
        }

        .custom-subscribe-btn:disabled {
          background-color: #48dbb2 !important;
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }

        .custom-subscribe-btn:disabled:hover {
          background-color: #48dbb2 !important;
        }

        @media (max-width: 768px) {
          .custom-subscribe-btn {
            padding: 8px 4px !important;
            width: 120px !important;
            font-size: 14px !important;
          }
        }

        @media (min-width: 1024px) {
          .custom-subscribe-btn {
            width: 173px !important;
          }
        }

        :global(#mc-embedded-subscribe-form .mc_fieldset) {
          border: none;
          min-height: 0px;
          padding-bottom: 0px;
        }

        :global(#mergeRow-gdpr) {
          margin-top: 20px;
          padding: 20px 0;
        }

        :global(#mergeRow-gdpr .content__gdpr label) {
          font-family: "Poppins", sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: white;
          margin-bottom: 10px;
          display: block;
        }

        :global(#mergeRow-gdpr .content__gdpr p) {
          font-family: "Poppins", sans-serif;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.5;
          margin: 10px 0;
        }

        :global(.checkbox.subfield) {
          display: inline-flex !important;
          align-items: center !important;
          margin: 15px 0 !important;
          cursor: pointer !important;
          font-family: " Poppins", sans-serif !important;
          font-size: 16px !important;
          color: #abb0b2 !important;
          font-weight: 500 !important;
          width: auto !important;
          max-width: none !important;
        }

        :global(.checkbox.subfield span) {
          display: inline !important;
        }

        :global(.checkbox.subfield input[type="checkbox"]) {
          width: 18px !important;
          height: 18px !important;
          margin: 0 8px 0 0 !important;
          accent-color: #48dbb2 !important;
          cursor: pointer !important;
          flex-shrink: 0 !important;
          display: inline-block !important;
        }

        :global(.mc_fieldset .checkbox) {
          display: inline-flex !important;
          width: auto !important;
        }

        :global(.content__gdprLegal) {
          margin-top: 20px;
          padding-top: 15px;
        }

        :global(.content__gdprLegal p) {
          font-family: "Poppins", sans-serif;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.4;
          margin: 0;
        }

        :global(.content__gdprLegal a) {
          color: #48dbb2;
          text-decoration: underline;
        }

        :global(.content__gdprLegal a:hover) {
          color: #3bc4a0;
        }

        :global(.indicates-required) {
          display: none;
        }

        .custom-form-container {
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          border-radius: 20px;
          overflow: hidden;
        }

        .custom-email-input:focus {
          background-color: #e5e5e5;
          transition: background-color 0.3s ease;
        }

        .custom-subscribe-btn:active {
          transform: translateY(1px);
          transition: transform 0.1s ease;
        }

        :global(#mce-error-response),
        :global(#mce-success-response) {
          transition: opacity 0.3s ease;
        }
      `}</style>

      <div
        id="mc_embed_shell"
        style={{
          padding: "0px 0 0px 0",
          borderTop: "1px solid #335c73",
          borderBottom: "1px solid #335c73",
        }}
      >
        <div
          id="mc_embed_signup"
          style={{
            background: "transparent",
            margin: "0 auto",
            padding: "0 0px",
          }}
        >
          <form
            action="https://seforall.us3.list-manage.com/subscribe/post?u=7538fc5f6603b063a8158282f&id=ac24bbda48&f_id=00b8cbe0f0"
            method="post"
            id="mc-embedded-subscribe-form"
            name="mc-embedded-subscribe-form"
            className="validate"
            target="_blank"
          >
            <h1
              className="mt-11"
              style={{
                textAlign: "center",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "32px",
                fontWeight: 600,
                color: "white",
                marginBottom: "32px",
                lineHeight: "normal",
              }}
            >
              Stay Connected
            </h1>

            <div className="custom-form-container">
              <input
                type="email"
                name="EMAIL"
                className="required email custom-email-input"
                id="mce-EMAIL"
                required
                defaultValue=""
                placeholder="Your E-Mail"
              />
              <input
                type="submit"
                name="subscribe"
                id="mc-embedded-subscribe"
                className="button custom-subscribe-btn"
                value="Subscribe"
              />
            </div>

            <div
              style={{ position: "absolute", left: "-5000px" }}
              aria-hidden="true"
            >
              <input
                type="text"
                name="b_7538fc5f6603b063a8158282f_ac24bbda48"
                tabIndex={-1}
                defaultValue=""
              />
            </div>
            <div id="mce-responses" className="clear" style={{ margin: 0 }}>
              <div
                className="response"
                id="mce-error-response"
                style={{ display: "none", color: "red", padding: 0 }}
              />
              <div
                className="response"
                id="mce-success-response"
                style={{
                  display: "none",
                  color: "red",
                  background: "transparent",
                  padding: 0,
                }}
              />
            </div>

            <div id="mc_embed_signup_scroll">
              <div className="indicates-required" style={{ display: "none" }}>
                <span className="asterisk">*</span> indicates required
              </div>

              <div className="mc-field-group" style={{ display: "none" }}>
                <label htmlFor="mce-EMAIL">
                  Email Address <span className="asterisk">*</span>
                </label>
              </div>

              <div
                id="mergeRow-gdpr"
                className="mergeRow gdpr-mergeRow content__gdprBlock mc-field-group"
              >
                <div className="content__gdpr">
                  <label>Marketing Permissions</label>
                  <p>
                    Please select all the ways you would like to hear from
                    Mission Efficiency c/o Sustainable Energy for All:
                  </p>
                  <fieldset
                    className="mc_fieldset gdprRequired"
                    name="interestgroup_field"
                  >
                    <label className="checkbox subfield" htmlFor="gdpr192">
                      <input
                        type="checkbox"
                        name="gdpr[192]"
                        className="gdpr"
                        value="Y"
                        id="gdpr192"
                      />
                      <span>Email</span>
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                  </fieldset>
                  <p>
                    You can unsubscribe at any time by clicking the link in the
                    footer of our emails. For information about our privacy
                    practices, please visit our website.
                  </p>
                </div>

                <div className="content__gdprLegal">
                  <p>
                    We use Mailchimp as our marketing platform. By clicking
                    below to subscribe, you acknowledge that your information
                    will be transferred to Mailchimp for processing.{" "}
                    <a href="https://mailchimp.com/legal/terms">Learn more</a>{" "}
                    about Mailchimp&apos;s privacy practices.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Script
        src="//s3.amazonaws.com/downloads.mailchimp.com/js/mc-validate.js"
        strategy="lazyOnload"
      />

      <Script id="mailchimp-init" strategy="lazyOnload">
        {`
          (function ($) {
            window.fnames = new Array();
            window.ftypes = new Array();
            fnames[0] = "EMAIL";
            ftypes[0] = "email";
            fnames[1] = "FNAME";
            ftypes[1] = "text";
            fnames[2] = "LNAME";
            ftypes[2] = "text";
            fnames[3] = "ADDRESS";
            ftypes[3] = "address";
            fnames[4] = "PHONE";
            ftypes[4] = "phone";
            fnames[5] = "MMERGE5";
            ftypes[5] = "text";
            fnames[6] = "MMERGE6";
            ftypes[6] = "text";
            fnames[7] = "MMERGE7";
            ftypes[7] = "dropdown";
          })(jQuery);
          var $mcj = jQuery.noConflict(true);
        `}
      </Script>
    </>
  );
}
